<script setup>
import { ref, computed } from 'vue'
import { useLangStore } from '@/stores/lang'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useTranslaterStore } from '@/stores/translater'

import UTASmartCaptcha from '@/components/auth/UTASmartCaptcha.vue'
import checkValid from '@/helpers/checkValid'

const route = useRoute()

const langStore         = useLangStore()
const authStore         = useAuthStore()
const translaterStore   = useTranslaterStore()

const lang                  = computed(() => langStore.lang)
const requestCodeProcess    = computed(() => authStore.requestCodeProcess)
const requestCodeMsg        = computed(() => authStore.requestCodeMsg)
const authProcess           = computed(() => authStore.authProcess)
const authMsg               = computed(() => authStore.authMsg)
const smartCaptchaId        = computed(() => authStore.smartCaptchaId)

const getTranslate = translaterStore.getTranslate

const UTAForgotEmail    = ref('')
const UTAForgotCode     = ref(null)

const requestCode   = ref(true)
const isFirstTry    = ref(false)
const formErrors    = ref([])
const scriptErrors  = ref([])
const formInvalids  = ref({})
const forgotData    = ref({})

// Ошибки скрипта каптчи
function setScriptError(error) {

    scriptErrors.value.push(error)
}

function handleSubmit() {

    formErrors.value = []
    formInvalids.value = {}

    forgotData.value = {
        code: lang.value, 
        login: {} 
    }

    if(requestCode.value) {

        if(UTAForgotEmail.value && checkValid(UTAForgotEmail.value, 'email')) {
            forgotData.value.login.email = UTAForgotEmail.value
        } else {
            formErrors.value.push(getTranslate.FORGOT_EMAIL)
            formInvalids.value.UTAForgotEmail = true
        }

        if(!window.smartCaptcha) {
            formErrors.value.push(getTranslate.SMART_CAPTCHA_ERROR_METHOD)
            formInvalids.value.UTAAuthCaptcha = true
        }

        if(formErrors.value.length == 0) {

            if(isFirstTry.value) { 
                window.smartCaptcha.reset(smartCaptchaId.value)
            }

            window.smartCaptcha.execute(smartCaptchaId.value)
        }

    } else {

        submitForgot()
    }
}

function submitForgot(smartCaptchaToken) {

    if(requestCode.value) {

        if(typeof smartCaptchaToken === "string" && smartCaptchaToken.length > 0) {
            forgotData.value.captcha = {
                yandex: {
                    token: smartCaptchaToken
                }
            }
        } else {
            formErrors.value.push(getTranslate.REGISTRATION_CAPTCHA)
            formInvalids.value.UTAAuthCaptcha = true
        }

        if(formErrors.value.length == 0) {
            authStore.sendRequestCode(forgotData.value).then(res => requestCode.value = res ? false : true )

            if(!isFirstTry.value) isFirstTry.value = true
        } else {
            window.smartCaptcha.reset(smartCaptchaId.value)
        }

    } else {

        const data = { 
            login: { 
                email: UTAForgotEmail.value
            }
        }

        if(UTAForgotCode.value) {
            data.code = UTAForgotCode.value
        } else {
            formErrors.value.push(getTranslate.USER_CODE)
            formInvalids.value.UTAForgotCode = true
        }

        if(formErrors.value.length == 0) {
            authStore.sendAuth(data)
        }
    }
}

if(route.query.name && route.query.code) {
    authStore.sendAuth({
        data: {
            code: route.query.code, 
            login: {
                email: route.query.name
            }
        }
    })
}
</script>

<template>
    <div class="uta-forgot">
        <div class="uta-forgot__header">
            <img src="@/assets/images/logo.png" alt="" class="uta-forgot__header-img">
        </div>
        <form action="" @submit.prevent="handleSubmit" class="uta-forgot__form">
            <div class="mb-4 uta-alert uta-alert--danger" v-if="formErrors.length">
                <span class="material-icons uta-alert__icon">error_outline</span>
                <div class="uta-alert__text">
                    {{ getTranslate.EFormFields }}:
                    <span class="uta-alert__text-item" v-for="(error, index) in formErrors" :key="error">
                        {{ error }}{{ index !== formErrors.length - 1 ? ', ' : '' }} 
                    </span>
                </div>
                <button type="button" class="uta-alert__close" @click="formErrors = [], formInvalids = {}">
                    <span class="material-icons">
                        close
                    </span>
                </button>
            </div>
            <div 
                v-if="'success' in requestCodeMsg && !requestCodeMsg.success"
                class="mb-4 uta-alert uta-alert--danger" 
            > 
                <span class="material-icons uta-alert__icon">
                    error_outline
                </span>
                <div class="uta-alert__text">
                    {{ getTranslate[requestCodeMsg.text] || requestCodeMsg.text }}
                </div>
                <button type="button" class="uta-alert__close" @click="authStore.setRequestCodeMsg({})">
                    <span class="material-icons">
                        close
                    </span>
                </button>
            </div>
            <template v-if="requestCode">
                <input 
                    class="mb-3 uta-input uta-input--block uta-forgot__input" 
                    :class="{ 'uta-input--invalid' : formInvalids.UTAForgotEmail }"
                    type="text" 
                    id="UTAForgotEmail" 
                    autocomplete="off"
                    :placeholder="getTranslate.FORGOT_EMAIL"
                    @input="delete formInvalids.UTAForgotEmail"
                    v-model.trim="UTAForgotEmail"
                >
                <button type="submit" class="uta-btn uta-btn--main uta-btn--block uta-btn--lg uta-forgot__submit">
                    <template v-if="!requestCodeProcess">
                        {{ getTranslate.BTN_REQUEST }}
                    </template>
                    <div class="loader" v-if="requestCodeProcess">
                        <div class="spinner-border text-secondary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </button>
            </template>
            <template v-else>
                <div class="my-4 uta-alert uta-alert--success">
                    {{ getTranslate.FORGOT_CODE_DESCR }}
                </div>
                <div class="mb-4 uta-alert uta-alert--danger" v-if="'success' in authMsg && !authMsg.success">
                    <span class="material-icons uta-alert__icon">error_outline</span>
                    <div class="uta-alert__text">
                        {{ getTranslate[authMsg.text] || authMsg.text }}
                    </div>
                    <button type="button" class="uta-alert__close" @click="authStore.setAuthMsg({})">
                        <span class="material-icons">
                            close
                        </span>
                    </button>
                </div>
                <input 
                    class="mb-3 uta-input uta-input--block" 
                    :class="{ 'uta-input--invalid' : formInvalids.UTAForgotCode }"
                    type="text" 
                    id="UTAForgotCode" 
                    autocomplete="off"
                    :placeholder="getTranslate.FORGOT_CODE"
                    @input="delete formInvalids.UTAForgotCode"
                    v-model.trim="UTAForgotCode"
                >
                <button type="submit" class="uta-btn uta-btn--main uta-btn--block uta-btn--lg uta-forgot__submit">
                    <template v-if="!authProcess">
                        {{ getTranslate.BTN_SEND }}
                    </template>
                    <div class="loader" v-if="authProcess">
                        <div class="spinner-border text-secondary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </button>
            </template>
            <UTASmartCaptcha @getToken="submitForgot" @error="setScriptError" />
            <div class="uta-auth__bottom">
                <RouterLink to="/registration" class="uta-auth__bottom-link uta-auth__bottom-link">{{ getTranslate.MENU_REGISTRATION }}</RouterLink>
                <RouterLink to="/auth" class="uta-auth__bottom-link uta-auth__bottom-link">{{ getTranslate.MENU_AUTH }}</RouterLink>
            </div>
        </form>
    </div>
</template>

<style lang="scss">
@import '@/assets/styles/scss/components/forgot';
</style>